import { useEffect, useState, useMemo } from 'react';
import { createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { format } from 'date-fns';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import { Input } from '@/client/components/ui/input';
import { Button } from '@/client/components/ui/button';
import { Badge } from '@/client/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import {
  RefreshCw,
  ChevronRight,
  Building2,
  AlertCircle,
  X,
} from 'lucide-react';
import { API_BASE_URL } from '@/client/config';
import type {
  OTTClient,
  OTTApp,
  GetOTTManagerResponse,
  AppSubmissionStatus,
} from '@shared/types';

/** Status filter options */
type StatusFilter = 'active' | 'all' | AppSubmissionStatus | 'no-status';

/** Status filter configuration */
const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'active', label: 'Active (Published, Review, Pending, Rejected)' },
  { value: 'all', label: 'All Statuses' },
  { value: 'Published', label: 'Published' },
  { value: 'Review', label: 'In Review' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Draft', label: 'Draft' },
  { value: 'no-status', label: 'No Status' },
];

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'ott-manager',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: '/',
      });
    }
  },
  component: OTTManagerComponent,
});

/** Platform brand colors */
export const PLATFORM_COLORS: Record<string, { bg: string; text: string }> = {
  FireTV: { bg: '#FF9900', text: '#000000' },
  AppleTV: { bg: '#000000', text: '#FFFFFF' },
  Roku: { bg: '#662D91', text: '#FFFFFF' },
  SamsungTV: { bg: '#1428A0', text: '#FFFFFF' },
  Android: { bg: '#3DDC84', text: '#000000' },
  IOS: { bg: '#555555', text: '#FFFFFF' },
  WebPage: { bg: '#475569', text: '#FFFFFF' },
};

/** Status colors */
export const STATUS_COLORS: Record<AppSubmissionStatus, { bg: string; text: string }> = {
  Draft: { bg: '#6B7280', text: '#FFFFFF' },
  Pending: { bg: '#3B82F6', text: '#FFFFFF' },
  Review: { bg: '#EAB308', text: '#000000' },
  Published: { bg: '#22C55E', text: '#FFFFFF' },
  Rejected: { bg: '#EF4444', text: '#FFFFFF' },
};

/** Format date for display */
export function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'MMM d, yyyy');
  } catch {
    return '-';
  }
}

/** Status badge component */
export function StatusBadge({ status }: { status: AppSubmissionStatus | null }) {
  if (!status) {
    return (
      <Badge variant="outline" className="text-muted-foreground text-xs">
        No Status
      </Badge>
    );
  }

  const colors = STATUS_COLORS[status];
  return (
    <Badge
      className="border-0 text-xs"
      style={{ backgroundColor: colors.bg, color: colors.text }}
    >
      {status}
    </Badge>
  );
}

/** Platform badge component */
export function PlatformBadge({ platform }: { platform: string }) {
  const colors = PLATFORM_COLORS[platform];
  return (
    <Badge
      className="text-xs border-0"
      style={colors ? { backgroundColor: colors.bg, color: colors.text } : undefined}
      variant={colors ? undefined : 'outline'}
    >
      {platform}
    </Badge>
  );
}

/** Get aggregated status for an app (worst status across all targets) */
function getAppStatus(app: OTTApp): AppSubmissionStatus | null {
  const statuses = app.storeTargets
    .map((t) => t.currentSubmission?.statusName)
    .filter(Boolean) as AppSubmissionStatus[];

  if (statuses.length === 0) return null;

  const statusPriority: Record<AppSubmissionStatus, number> = {
    Rejected: 0,
    Draft: 1,
    Pending: 2,
    Review: 3,
    Published: 4,
  };

  return statuses.reduce((worst, current) =>
    statusPriority[current] < statusPriority[worst] ? current : worst
  );
}

/** Get the client updated date (manifest last edited) */
function getClientUpdatedAt(app: OTTApp): string | null {
  return app.manifestUpdatedAt;
}

/** Get the most recent vendor update date across all targets */
function getVendorUpdatedAt(app: OTTApp): string | null {
  const dates = app.storeTargets
    .map((t) => t.currentSubmission?.vendorUpdatedAt)
    .filter((d): d is string => d !== null);

  if (dates.length === 0) return null;

  return dates.reduce((latest, current) =>
    new Date(current) > new Date(latest) ? current : latest
  );
}

/** Check if app has any rejections */
function hasRejections(app: OTTApp): boolean {
  return app.storeTargets.some((t) =>
    t.submissions.some((s) => s.statusName === 'Rejected')
  );
}

/** Single app row */
function AppRow({ app, onClick }: { app: OTTApp; onClick: () => void }) {
  const status = getAppStatus(app);
  const clientUpdated = getClientUpdatedAt(app);
  const vendorUpdated = getVendorUpdatedAt(app);
  const showRejectionWarning = hasRejections(app);

  return (
    <div
      onClick={onClick}
      className="flex items-center justify-between py-3 px-4 hover:bg-accent/50 cursor-pointer rounded-lg group"
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        {/* App name */}
        <span className="font-medium min-w-[180px] max-w-[180px] truncate" title={app.appName}>
          {app.appName}
        </span>

        {/* Platform badges */}
        <div className="flex flex-wrap flex-1 gap-1 min-w-[200px] max-w-[280px]">
          {app.storeTargets.map((target) => (
            <PlatformBadge key={target.idx} platform={target.targetName} />
          ))}
        </div>

        {/* Client updated (manifest edited) */}
        <span className="text-sm flex-1 text-muted-foreground min-w-[100px]">
          {formatDate(clientUpdated)}
        </span>

        {/* Vendor updated (submission workflow) */}
        <span className="text-sm flex-1 text-muted-foreground min-w-[100px]">
          {formatDate(vendorUpdated)}
        </span>

        {/* Status + rejection warning */}
        <div className="flex flex-1 items-center gap-2 min-w-[120px]">
          <StatusBadge status={status} />
          {showRejectionWarning && (
            <span title="Has rejections">
              <AlertCircle className="h-4 w-4 text-red-500" />
            </span>
          )}
        </div>

      </div>

      {/* Arrow indicator */}
      <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

/** Client section with header and app list */
function ClientSection({ client }: { client: OTTClient }) {
  const navigate = useNavigate();

  // Sort apps alphabetically
  const sortedApps = useMemo(
    () => [...client.apps].sort((a, b) => a.appName.localeCompare(b.appName)),
    [client.apps]
  );

  const handleAppClick = (app: OTTApp) => {
    navigate({ to: '/ott-manager/$apIdx', params: { apIdx: String(app.apIdx) } });
  };

  return (
    <div className="mb-8 pb-6 border-b border-border/50 last:border-b-0">
      {/* Client header */}
      <div className="flex items-center gap-2 mb-3 px-2">
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <h3 className="font-semibold text-lg">{client.clientName}</h3>
        <span className="text-sm text-muted-foreground">
          ({client.apps.length} app{client.apps.length !== 1 ? 's' : ''})
        </span>
      </div>

      {/* App rows */}
      <div className="ml-10 space-y-1">
        {sortedApps.map((app) => (
          <AppRow key={app.apIdx} app={app} onClick={() => handleAppClick(app)} />
        ))}
      </div>
    </div>
  );
}

function OTTManagerComponent() {
  const { getAccessTokenSilently } = useAuth0();
  const [clients, setClients] = useState<OTTClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [clientFilter, setClientFilter] = useState<string>('all');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/ott-manager`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }

      const data: GetOTTManagerResponse = await response.json();
      setClients(data.clients);
    } catch (err) {
      console.error('Error fetching OTT data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [getAccessTokenSilently]);

  /** Check if an app matches the status filter */
  const appMatchesStatusFilter = (app: OTTApp): boolean => {
    const appStatus = getAppStatus(app);

    switch (statusFilter) {
      case 'all':
        return true;
      case 'active':
        // Show everything except Draft and No Status
        return appStatus !== null && appStatus !== 'Draft';
      case 'no-status':
        return appStatus === null;
      default:
        // Specific status filter
        return appStatus === statusFilter;
    }
  };

  // Sort clients alphabetically, then filter
  const sortedAndFilteredClients = useMemo(() => {
    // First sort alphabetically
    const sorted = [...clients].sort((a, b) =>
      a.clientName.localeCompare(b.clientName)
    );

    // Apply client filter first
    const clientFiltered = clientFilter && clientFilter !== 'all'
      ? sorted.filter((c) => c.clientID === clientFilter)
      : sorted;

    const search = searchFilter.toLowerCase();

    return clientFiltered
      .map((client) => {
        const clientNameMatches = search && client.clientName.toLowerCase().includes(search);

        let filteredApps = client.apps.filter((app) => {
          // First check status filter
          if (!appMatchesStatusFilter(app)) return false;

          // Then check text search
          if (!search) return true;

          const appMatches = app.appName.toLowerCase().includes(search);
          const platformMatches = app.storeTargets.some((t) =>
            t.targetName.toLowerCase().includes(search)
          );
          const statusMatches = app.storeTargets.some((t) =>
            t.currentSubmission?.statusName?.toLowerCase().includes(search)
          );
          return appMatches || platformMatches || statusMatches;
        });

        // If client name matches search, show all apps that pass status filter
        if (clientNameMatches) {
          filteredApps = client.apps.filter(appMatchesStatusFilter);
        }

        if (filteredApps.length > 0) {
          return { ...client, apps: filteredApps };
        }
        return null;
      })
      .filter(Boolean) as OTTClient[];
  }, [clients, searchFilter, statusFilter, clientFilter]);

  // Count totals
  const totalApps = clients.reduce((sum, c) => sum + c.apps.length, 0);
  const totalTargets = clients.reduce(
    (sum, c) => sum + c.apps.reduce((s, a) => s + a.storeTargets.length, 0),
    0
  );

  return (
    <Layout>
      <div>
        <section>
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-2xl font-semibold">OTT Manager</h3>
            {!loading && !error && clients.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {clients.length} clients · {totalApps} apps
              </span>
            )}
          </div>

          <p className="text-muted-foreground mb-6">
            Manage OTT app submissions across all clients. Click an app to view submission details.
          </p>

          {loading && <p>Loading...</p>}

          {error && (
            <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded mt-4">
              <p className="m-0 text-red-800 font-medium">Error: {error}</p>
            </div>
          )}

          {!loading && !error && clients.length === 0 && (
            <p>No OTT clients found.</p>
          )}

          {!loading && !error && clients.length > 0 && (
            <>
              {/* Filters */}
              <div className="flex flex-wrap items-end gap-4 mb-6">
                {/* Status filter */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-muted-foreground">Status</label>
                  <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                    <SelectTrigger className="w-[280px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_FILTER_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Client filter */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-muted-foreground">Client</label>
                  <div className="flex items-center gap-1">
                    <Select value={clientFilter} onValueChange={setClientFilter}>
                      <SelectTrigger className="w-[220px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Clients</SelectItem>
                        {clients
                          .slice()
                          .sort((a, b) => a.clientName.localeCompare(b.clientName))
                          .map((c) => (
                            <SelectItem key={c.clientID} value={c.clientID}>
                              {c.clientName}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {clientFilter && clientFilter !== 'all' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setClientFilter('all')}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Text search */}
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-xs font-medium text-muted-foreground">Search</label>
                  <Input
                    placeholder="Filter by app name, platform..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    className="max-w-sm"
                  />
                </div>

                {/* Refresh */}
                <div className="ml-auto">
                  <Button
                    variant="outline"
                    onClick={fetchData}
                    disabled={loading}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>

              {/* Column headers */}
              <div className="flex items-center gap-4 px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide border-b border-border/50 mb-4 ml-6">
                <span className="min-w-[180px]">App</span>
                <span className="min-w-[200px] max-w-[280px] flex-1">Platforms</span>
                <span className="min-w-[100px] flex-1">Client Updated</span>
                <span className="min-w-[100px] flex-1">Vendor Updated</span>
                <span className="min-w-[120px] flex-1">Status</span>
              </div>

              {/* Client sections */}
              <div>
                {sortedAndFilteredClients.map((client) => (
                  <ClientSection key={client.clientID} client={client} />
                ))}
              </div>

              {searchFilter && sortedAndFilteredClients.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  No results match "{searchFilter}"
                </p>
              )}

              {searchFilter && sortedAndFilteredClients.length > 0 && (
                <p className="mt-4 text-sm text-muted-foreground">
                  Showing {sortedAndFilteredClients.length} of {clients.length} clients
                </p>
              )}
            </>
          )}
        </section>
      </div>
    </Layout>
  );
}
